import {Component, Input, OnInit} from '@angular/core';
import {EndUser, Trunk} from '@gt-api';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {GenericSearchTableActionsDirective} from '@app/service-provider/util/generic-search-table-actions.directive';
import {TableEntry} from '@app/admin/customer-controlled-modules/modules/util/table-entry';
import {GtPhoneNumberUtil} from '@app/shared/utils/gt-phone-number.util';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {EndUserTerminal} from '@app/services/end-user-terminal';
import {GtDirectoryService} from '@app/services/gt-directory.service';
import {
  EndUserTerminalDialogData,
  EndUserTerminalEditorComponent
} from '@app/service-provider/end-user-terminal-editor/end-user-terminal-editor.component';
import {MatDialog} from '@angular/material/dialog';
import {getTerminalCategories, getTerminalTypes} from '@app/shared/utils/terminal-utils';
import {ConfirmDeleteDialogComponent} from '@app/shared/confirm-delete-dialog/confirm-delete-dialog.component';
import {checkResponse} from '@app/shared/utils/app-utils';
import {EMPTY} from 'rxjs';
import {GtNotificationService} from '@app/services/gt-notification.service';

interface EndUserTerminalTableEntry extends TableEntry<EndUserTerminal> {
  index: number;
  fullNumber: string;
  shortNumber: string;
  terminalType: number;
  outboundTriggering: boolean;
  inboundTriggering: boolean;
  category: number;
  trunkProvider: string;
  trunkId: string;
  enabled: boolean;
}

@Component({
  selector: 'gt-end-user-terminal-list',
  templateUrl: './end-user-terminal-list.component.html',
  styleUrls: ['./end-user-terminal-list.component.scss']
})
export class EndUserTerminalListComponent
  extends GenericSearchTableActionsDirective<EndUserTerminalTableEntry, EndUserTerminal>
  implements OnInit {
  @Input() endUser: EndUser;
  dataSourceColumns = [
    'index',
    'fullNumber',
    'shortNumber',
    'terminalType',
    'inboundTriggering',
    'outboundTriggering',
    'category',
    'trunkProvider',
    'trunkId',
    'enabled',
    'actions'
  ];

  terminalTypes = getTerminalTypes();
  terminalCategories = getTerminalCategories();
  private trunkProviders: string[];
  private trunks: Map<string, Array<Trunk>>;

  constructor(
    public dialog: MatDialog,
    private notificationService: GtNotificationService,
    private gtAuthenticationService: GtAuthenticationService,
    private gtDirectoryService: GtDirectoryService
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadData();
  }

  private loadData() {
    this.gtDirectoryService
      .getTrunks()
      .pipe(
        tap(trunks => {
          this.trunkProviders = Array.from(trunks.keys());
          this.trunks = trunks;
        }),
        switchMap(() => this.gtDirectoryService.getEndUserTerminals(this.endUser.organization, this.endUser.login)),
        tap(endUserTerminals => {
          this.dataSource.data = endUserTerminals.map(d => this.newTableEntry(d, false));
        })
      )
      .subscribe();
  }

  newTableEntry(entry: EndUserTerminal, editing: boolean): EndUserTerminalTableEntry {
    return {
      entry,
      editing,
      index: entry.idx,
      fullNumber: GtPhoneNumberUtil.toDisplay(entry.fullNumber),
      shortNumber: entry.shortNumber,
      terminalType: entry.terminalType,
      outboundTriggering: entry.outboundTriggering,
      inboundTriggering: entry.inboundTriggering,
      category: entry.category,
      trunkProvider: entry.trunkProvider,
      trunkId: entry.trunkId,
      enabled: entry.enabled
    };
  }

  saveRow(entry: EndUserTerminalTableEntry): void {}

  search(key: string): void {}

  hasAddEndUserRights(): boolean {
    return this.gtAuthenticationService.endUserAccessRights.add;
  }

  hasEditEndUserRights(): boolean {
    return this.gtAuthenticationService.endUserAccessRights.edit;
  }

  addRow() {
    if (this.hasAddEndUserRights()) {
      const dialogData: EndUserTerminalDialogData = {
        entry: {
          organization: this.endUser.organization,
          login: this.endUser.login
        },
        editMode: false,
        trunkProviders: this.trunkProviders,
        trunks: this.trunks
      };

      this.openDialog(dialogData);
    }
  }

  openDeleteDialog(entry: EndUserTerminalTableEntry) {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      autoFocus: false,
      data: entry.fullNumber
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteTerminal(entry);
      }
    });
  }

  private deleteTerminal(entry: EndUserTerminalTableEntry) {
    const terminal = entry.entry;
    this.gtDirectoryService
      .removeEndUserTerminal(terminal.organization, terminal.login, terminal.fullNumber)
      .pipe(
        checkResponse(),
        tap(() => {
          this.loadData();
          this.notificationService.showSuccessMessage();
        }),
        catchError(error => {
          this.notificationService.showErrorMessage(error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  editRow(entry: EndUserTerminalTableEntry) {
    if (this.hasEditEndUserRights()) {
      const endUserTerminal = {...entry.entry};
      if (!endUserTerminal.trunkProvider) {
        const firstTrunkProvider = this.trunkProviders[0];
        const trunk = this.trunks.get(firstTrunkProvider)[0];
        endUserTerminal.trunkProvider = firstTrunkProvider;
        endUserTerminal.trunkId = trunk.id;
      }
      const dialogData: EndUserTerminalDialogData = {
        entry: endUserTerminal,
        editMode: true,
        trunkProviders: this.trunkProviders,
        trunks: this.trunks
      };

      this.openDialog(dialogData);
    }
  }

  openDialog(dialogData: EndUserTerminalDialogData) {
    const dialogRef = this.dialog.open(EndUserTerminalEditorComponent, {
      minWidth: 500,
      disableClose: true,
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(updateSearch => {
      if (updateSearch) {
        this.loadData();
      }
    });
  }
}
